import { ChangeDetectionStrategy, Component, inject, OnInit, viewChild } from '@angular/core';

import { AsyncPipe, CommonModule } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import {
  selectAllMembersSelected,
  selectDealerGroupCount,
  selectDisplayText,
  selectedGroupMemberUuids,
  selectSelectedDealersCount,
  selectSingleDealerGroupName
} from '../../services/lead-seller-group/reducer';
import { DealerGroupsState } from '../../interfaces/dealer-group.interface';
import { LeadSellerGroupService } from '../../services/lead-seller-group/lead-seller-group.service';
import { take, takeUntil } from 'rxjs/operators';
import { PopoverDirective, PopoverModule } from 'ngx-bootstrap/popover';
import { DealerGroupSelectionSectionComponent } from '../dealer-group-selection-section/dealer-group-selection-section.component';
import { HeaderSearchExecutionService } from '../../services/header-search-execution/header-search-execution.service';
import { SaComponent } from 'smartauction-frontend-micro-library';

enum ButtonDisplayText {
  ALL_GROUPS = 'ALL_GROUPS',
  NO_GROUPS = 'NO_GROUPS',
  MULTI_GROUPS = 'MULTI_GROUPS'
}

@Component({
  selector: 'app-dealer-group-selection',
  templateUrl: './dealer-group-selection.component.html',
  styleUrl: './dealer-group-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PopoverModule, CommonModule, AsyncPipe, DealerGroupSelectionSectionComponent],
  standalone: true
})
export class DealerGroupSelectionComponent extends SaComponent implements OnInit {
  readonly popover$ = viewChild<PopoverDirective>('popover');
  private readonly translocoService = inject(TranslocoService);
  private readonly headerSearchExecutionService = inject(HeaderSearchExecutionService);
  private readonly leadSellerGroupService: LeadSellerGroupService;
  displayText: string;
  buttonText$: Observable<string>;
  totalSelected$: Observable<number>;
  singleLeadSellerGroup$: Observable<string>;
  singleLeadSellerGroupName: string;
  preState: DealerGroupsState;
  open = false;
  dealerGroups$: Observable<DealerGroupsState>;
  allSelected$: Observable<boolean>;
  dealershipGroupCount = 0;

  translations = {
    labelSearch: '',
    labelAllGroups: '',
    btnSave: ''
  };

  dealerGroupTranslations = {
    allGroups: '',
    noGroups: '',
    multiGroups: ''
  };

  constructor() {
    super();
    this.leadSellerGroupService = inject(LeadSellerGroupService);
    this.dealerGroups$ = this.leadSellerGroupService.state$;
    this.allSelected$ = this.leadSellerGroupService.select(selectAllMembersSelected);
    this.buttonText$ = this.leadSellerGroupService.select(selectDisplayText);
    this.totalSelected$ = this.leadSellerGroupService.select(selectSelectedDealersCount);
    this.singleLeadSellerGroup$ = this.leadSellerGroupService.select(selectSingleDealerGroupName);
  }

  ngOnInit(): void {
    this.singleLeadSellerGroup$.subscribe(res => {
      this.singleLeadSellerGroupName = res;
    });

    this.buttonText$.subscribe(response => {
      this.getGroupSelectTranslations(response);
    });

    if (this.singleLeadSellerGroupName) {
      this.displayText = this.singleLeadSellerGroupName;
    }

    this.leadSellerGroupService.search('');
    this.leadSellerGroupService.select(selectDealerGroupCount).subscribe(groupCount => {
      this.dealershipGroupCount = groupCount;
    });
  }

  getButtonDisplayTextTranslation(response) {
    switch (response) {
      case ButtonDisplayText.ALL_GROUPS:
        this.displayText = this.dealerGroupTranslations.allGroups;
        break;
      case ButtonDisplayText.NO_GROUPS:
        this.displayText = this.dealerGroupTranslations.noGroups;
        break;
      case ButtonDisplayText.MULTI_GROUPS:
        this.displayText = this.dealerGroupTranslations.multiGroups;
        break;
    }
  }

  getGroupSelectTranslations(response) {
    const keyParams = {
      'UIComponents.dealerGroupTranslations': {},
      'sellModule.manageInventory.dealerGroupPopover': {},
      common: {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.dealerGroupTranslations.allGroups = translate[0].allGroups;
        this.dealerGroupTranslations.noGroups = translate[0].noGroups;
        this.dealerGroupTranslations.multiGroups = translate[0].multiGroups;
        this.translations.labelAllGroups = translate[1].labelAllGroups;
        this.translations.labelSearch = translate[2].labelSearch;
        this.translations.btnSave = translate[2].btnSave;
        this.getButtonDisplayTextTranslation(response);
      });
  }

  saveAndSearch() {
    this.preState = null;
    this.popover$().hide();
    const currentDealerGroupState = this.leadSellerGroupService.snapShot();
    this.headerSearchExecutionService.executeSearchOnLeadSellerGroupMemberChange(
      selectedGroupMemberUuids(currentDealerGroupState)
    );
    this.leadSellerGroupService.cacheState();
  }

  onShown() {
    this.leadSellerGroupService.state$.pipe(take(1)).subscribe(state => (this.preState = state));
  }

  toggleAll() {
    this.leadSellerGroupService.toggleAll();
  }

  search(e) {
    this.leadSellerGroupService.search(e.target.value.toLowerCase().trim());
  }

  toggleGroup(groupUuid: string) {
    this.leadSellerGroupService.toggleGroup(groupUuid);
  }

  toggleMember(uuid: string) {
    this.leadSellerGroupService.toggleMember(uuid);
  }

  get isSellerAssociatedToMultipleLeadSellerGroup() {
    return this.dealershipGroupCount && this.dealershipGroupCount > 1;
  }
}
